export const PENDING_DESTINATION_STORAGE_KEY = 'beoflow.pending-destination'
const PENDING_DESTINATION_TTL_MS = 7 * 24 * 60 * 60 * 1000

export type DestinationKind =
  | 'consent-invite'
  | 'shared-workout'
  | 'shared-folder'
  | 'shared-plan'
  | 'shared-view'
  | 'team-invite'
  | 'plan-editor-invite'
  | 'activity-comment'

export interface PendingDestination {
  kind: DestinationKind
  key: string
  capturedAt: number
}

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}'
const HEX_TOKEN = '[0-9a-f]{32}'
const BASE64URL_TOKEN = '[A-Za-z0-9_-]{32}'

const DESTINATIONS: Record<DestinationKind, { key: RegExp; paths: string[]; route: string }> = {
  'consent-invite': { key: new RegExp(`^${UUID}$`, 'i'), paths: ['/r/consent-invite/', '/invite/'], route: '/invite/' },
  'shared-workout': { key: new RegExp(`^${HEX_TOKEN}$`, 'i'), paths: ['/r/shared-workout/', '/workout/share/'], route: '/workout/share/' },
  'shared-folder': { key: new RegExp(`^${HEX_TOKEN}$`, 'i'), paths: ['/r/shared-folder/', '/folder/share/'], route: '/folder/share/' },
  'shared-plan': { key: new RegExp(`^${HEX_TOKEN}$`, 'i'), paths: ['/r/shared-plan/', '/plans/share/'], route: '/plans/share/' },
  'shared-view': { key: new RegExp(`^${BASE64URL_TOKEN}$`), paths: ['/r/shared-view/', '/shared/'], route: '/shared/' },
  'team-invite': { key: new RegExp(`^${UUID}$`, 'i'), paths: ['/r/team-invite/', '/team-invite/'], route: '/team-invite/' },
  'plan-editor-invite': { key: new RegExp(`^${UUID}$`, 'i'), paths: ['/r/plan-editor-invite/', '/plan-invite/'], route: '/plan-invite/' },
  'activity-comment': { key: new RegExp(`^${UUID}$`, 'i'), paths: ['/r/activity-comment/', '/activity-comment/'], route: '/activity-comment/' },
}

function isDestinationKind(value: string): value is DestinationKind {
  return Object.hasOwn(DESTINATIONS, value)
}

/** Parse only the typed content destinations defined by BEO-3043. Raw URLs,
 * auth credentials and arbitrary local routes are deliberately not persisted. */
export function pendingDestinationFromPath(value: string): PendingDestination | null {
  if (!value.startsWith('/') || value.startsWith('//') || value.includes('\\') || value.includes('%')) return null
  const pathname = value.split(/[?#]/, 1)[0]

  for (const [kindValue, config] of Object.entries(DESTINATIONS)) {
    const prefix = config.paths.find((candidate) => pathname.startsWith(candidate))
    if (!prefix) continue
    const key = pathname.slice(prefix.length)
    if (!key || key.includes('/') || !config.key.test(key)) return null
    return { kind: kindValue as DestinationKind, key, capturedAt: Date.now() }
  }
  return null
}

export function pendingDestinationPath(destination: Pick<PendingDestination, 'kind' | 'key'>): string | null {
  const config = DESTINATIONS[destination.kind]
  return config?.key.test(destination.key) ? `${config.route}${destination.key}` : null
}

export function rememberPendingDestinationFromPath(value: string): PendingDestination | null {
  const destination = pendingDestinationFromPath(value)
  if (!destination) return null
  try {
    localStorage.setItem(PENDING_DESTINATION_STORAGE_KEY, JSON.stringify(destination))
  } catch {
    return null
  }
  return destination
}

export function readPendingDestination(): PendingDestination | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(PENDING_DESTINATION_STORAGE_KEY) || 'null') as Partial<PendingDestination> | null
    if (!parsed || typeof parsed.kind !== 'string' || !isDestinationKind(parsed.kind) || typeof parsed.key !== 'string' || typeof parsed.capturedAt !== 'number') return null
    if (Date.now() - parsed.capturedAt > PENDING_DESTINATION_TTL_MS) {
      localStorage.removeItem(PENDING_DESTINATION_STORAGE_KEY)
      return null
    }
    return pendingDestinationPath({ kind: parsed.kind, key: parsed.key }) ? parsed as PendingDestination : null
  } catch {
    return null
  }
}

export function readPendingDestinationPath(): string | null {
  const destination = readPendingDestination()
  return destination ? pendingDestinationPath(destination) : null
}

export function clearPendingDestination(kind?: string, key?: string): void {
  try {
    if (kind || key) {
      const pending = readPendingDestination()
      if (!pending || pending.kind !== kind || pending.key !== key) return
    }
    localStorage.removeItem(PENDING_DESTINATION_STORAGE_KEY)
  } catch {
    // Storage may be unavailable in private browsing mode.
  }
}
